import { UnPinIcon } from "Areas/SharedFeatures/nodeLabel";
import { COLUMN } from "Areas/SharedFeatures/rightSideDetailsPanel";
import { NODE_SERVER_IGNORED_FIELDS } from "Areas/SharedFeatures/types/nodeSource";
import useBeforeUnloadMessage from "Common/componentUtils/useBeforeUnloadMessage";
import { SUMMARY_REPORT_OBJECT_TYPE } from "Common/dictionaries/summaryReportObjectType";
import { ZOOM_LEVEL } from "Common/dictionaries/zoomLevel";
import Menu from "Components/menu";
import ProgressOverlay from "Components/progressOverlay";
import { themeVariables } from "Components/theme";
import theme from "Components/theme/themeVariables";
import yFilesLicense from "Content/vendor/yWorks/yWorks.yFilesHTML.License.json";
import cloneDeep from "lodash/cloneDeep";
import flatMap from "lodash/flatMap";
import get from "lodash/get";
import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import keys from "lodash/keys";
import map from "lodash/map";
import noop from "lodash/noop";
import omit from "lodash/omit";
import values from "lodash/values";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Droppable } from "react-drag-and-drop";
import { useResizeDetector } from "react-resize-detector";
import { ThemeProvider } from "styled-components";
import { GraphBuilder, GraphItemTypes, ICommand, License, Point, VisualCachingPolicy, YGraphAdapter } from "yfiles";
import { READ_ONLY_MODE } from "../documentDetails/types/readOnlyMode";
import MethodsSubMenu from "../methodsSubMenu";
import { GRAPH_IN_PRESENTATION_MODE_CLASS_NAME, GRAPH_IN_PROGRESS_CLASS_NAME, GRAPH_IS_EXPORTING_CLASS_NAME, ZOOM } from "./constants";
import locale from "./graph.locale";
import GroupNodeLabel from "./groupNodeLabel";
import GroupNodeTable from "./groupNodeTable";
import GroupNodeTableIcon from "./groupNodeTableIcon";
import RightSideDetailsPanel from "./rightSideDetailsPanel";
import GraphPanel from "./graphPanel";
import buildGroupTableEventStopPropagation from "./utils/buildCallbacks/buildGroupTableEventStopPropagation";
import buildOnDropCallback from "./utils/buildCallbacks/buildOnDropCallback";
import buildOnGroupTableViewCallback from "./utils/buildCallbacks/buildOnGroupTableViewCallback";
import buildOnNodeOrEdgeDataUpdateCallback from "./utils/buildCallbacks/buildOnNodeOrEdgeDataUpdateCallback";
import { buildOnRowsPinCallback } from "./utils/buildCallbacks";
import { buildOnSaveCallback } from "./utils/buildCallbacks/buildOnSaveCallback";
import bindCommands from "./utils/commands/bindCommands";
import configureInputModes from "./utils/configure/configureInputModes";
import resizeGroupNodeDecorators from "./utils/configure/resizeGroupNodeDecorators";
import undoEngine from "./utils/configure/undoEngine";
import updateOrthogonalEdgeEditingContext from "./utils/configure/updateOrthogonalEdgeEditingContext";
import buildCopyCallback from "./utils/contextMenu/buildCopyCallback";
import buildDeleteCallback from "./utils/contextMenu/buildDeleteCallback";
import buildPasteCallback from "./utils/contextMenu/buildPasteCallback";
import { getFromLocalStorage } from "./utils/contextMenu/localStorageUtils";
import createGraphComponent from "./utils/createGraphComponent";
import ClientSideImageExport from "./utils/export/clientSideImageExport";
import filterGraphByInterval from "./utils/filterGraphByInterval";
import hideTasksResults from "./utils/hideTasksResults";
import highlightTasksResults from "./utils/highlightTasksResults";
import layoutAlgorithmMap from "./utils/layoutAlgorithms/layoutAlgorithmMap";
import addGraphListeners from "./utils/listeners/addGraphListeners";
import navigateToNode from "./utils/navigateToNode";
import getGraphNodeSources from "./utils/nodeOrEdgeSourceDataHandling/getGraphNodeSources";
import { updateGroupTable } from "./utils/nodeRendering/updateGroupNodeTable";
import { deserialize } from "./utils/serialization";
import initializeDefaultStyles from "./utils/styling/initializeDefaultStyles";
import applyUpdates from "./utils/updates/applyUpdates";
import restoreSelection from "./utils/updates/restoreSelection";
import { DOCUMENT_BG } from "../constants";
import { createProviderWebsocketProvider } from "Common/api/wsServer";
import { Doc } from "yjs";
import { RemoteCursorsGraph, RemoteCursorsProvider } from "Components/RemoteCursors";
import styles from "./graph.module.css";
import classNames from "classnames";
import { NeuralInterface } from "Areas/Documents/graph/NeuralInterface";
import { useGraphChangeListener, useGraphChangeNotifier } from "./utils/yjs/hooks";
License.value = yFilesLicense;
const detailsPanelTableColumns = [
    COLUMN.OBJECT_TYPE,
    COLUMN.ID,
    COLUMN.COLOR,
    COLUMN.INPUT_LINKS_COUNT,
    COLUMN.OUTPUT_LINKS_COUNT,
    COLUMN.PIN_ACTION
];
const clientSideImageExport = new ClientSideImageExport();
const groupTableIconPositionStyles = {
    position: "absolute",
    right: 10,
    top: 6
};
function Graph(props) {
    var _a, _b, _c, _d;
    const divRef = useRef(null);
    const sourceDataRef = useRef(props.sourceData);
    const graphComponentRef = useRef();
    const [graphComponent, setGraphComponent] = useState(null);
    const [provider, setProvider] = useState(null);
    const skipFitGraphBounds = useRef(false);
    const skipLoader = useRef(false);
    const hasChangesRef = useRef(Boolean(props.hasChanges));
    const initialHasChangesRef = useRef(Boolean(props.hasChanges));
    const initialDataWasLoadedRef = useRef(false);
    const timerRef = useRef(null);
    const graphDivDataMouseDownEventRef = useRef();
    const [sourceModelWasApplied, setSourceModelWasApplied] = useState(false);
    const graphModelAppliedRef = useRef({
        readyForListeners: false,
        updates: {
            NodesSource: {},
            EdgesSource: {},
            Groups: {}
        }
    });
    const [zoom, setZoom] = useState(ZOOM.DEFAULT);
    const layoutAlgorithmRef = useRef(props.layoutAlgorithm);
    const layoutAlgorithmTimerRef = useRef(null);
    const nodesLayoutsRef = useRef({});
    const nodeIdsPinnedOutOfGroupRef = useRef({});
    const [inProgress, setInProgress] = useState({
        value: Boolean(props.inProgress || props.saveGraphInProgress),
        presentationMode: false
    });
    const { height, ref: observableRef } = useResizeDetector();
    const documentNodeAttributeImageRef = useRef({});
    const activeIconWithClickStopPropagationRef = useRef(false);
    const contextMenuRef = useRef(null);
    const [hasPasteData, setHasPasteData] = useState(() => { var _a; return Boolean((_a = getFromLocalStorage()) === null || _a === void 0 ? void 0 : _a.FromDocumentId); });
    const graphAdapterRef = useRef();
    const canEditAndReadyForModifyRef = useRef(Boolean(props.canEdit && !props.presentationInterval));
    const canEditAndReadyForModify = useMemo(() => Boolean(props.canEdit && !props.presentationInterval), [props.canEdit, Boolean(props.presentationInterval)]);
    const readOnlyModeRef = useRef(props.readOnlyMode);
    useEffect(() => {
        canEditAndReadyForModifyRef.current = canEditAndReadyForModify;
    }, [canEditAndReadyForModify]);
    useEffect(() => {
        readOnlyModeRef.current = props.readOnlyMode;
    }, [props.readOnlyMode]);
    useEffect(() => {
        var _a;
        (_a = props.onInitialized) === null || _a === void 0 ? void 0 : _a.call(props);
        return () => {
            timerRef.current && clearTimeout(timerRef.current);
            layoutAlgorithmTimerRef.current && clearTimeout(layoutAlgorithmTimerRef.current);
        };
    }, []);
    useEffect(() => {
        hasChangesRef.current = Boolean(props.hasChanges);
    }, [props.hasChanges]);
    useEffect(() => {
        if (initialDataWasLoadedRef.current ||
            !props.documentId ||
            props.saveGraphInProgress ||
            !props.onGetGraphData)
            return;
        initialDataWasLoadedRef.current = true;
        props.onGetGraphData(props.documentId, false);
    }, [props.documentId, props.saveGraphInProgress]);
    useBeforeUnloadMessage(hasChangesRef);
    const handleChangeZoom = useCallback((newZoom) => {
        var _a;
        ((_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.hide) && contextMenuRef.current.hide();
        setZoom(newZoom);
    }, []);
    const setGraphInProgress = useCallback((newInProgress, presentationMode = false) => {
        var _a;
        if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.div.isConnected))
            return;
        setInProgress(prev => {
            if (!graphComponentRef.current ||
                prev.value === newInProgress ||
                (prev.presentationMode && !presentationMode))
                return prev;
            const divClassList = graphComponentRef.current.div.classList;
            if (newInProgress) {
                presentationMode && divClassList.add(GRAPH_IN_PRESENTATION_MODE_CLASS_NAME);
                divClassList.add(GRAPH_IN_PROGRESS_CLASS_NAME);
            }
            else {
                presentationMode && divClassList.remove(GRAPH_IN_PRESENTATION_MODE_CLASS_NAME);
                divClassList.remove(GRAPH_IN_PROGRESS_CLASS_NAME);
            }
            const inputMode = graphComponentRef.current.inputMode;
            if (inputMode) {
                const disable = !props.canEdit || newInProgress;
                inputMode.deletableItems = disable
                    ? GraphItemTypes.NONE
                    : GraphItemTypes.NODE | GraphItemTypes.EDGE;
                inputMode.movableItems = disable
                    ? GraphItemTypes.NONE
                    : GraphItemTypes.NODE | GraphItemTypes.BEND | GraphItemTypes.EDGE;
                inputMode.allowCreateEdge = !disable;
                inputMode.moveInputMode.enabled = !disable;
                inputMode.createBendInputMode.enabled = !disable;
                presentationMode && resizeGroupNodeDecorators(graphComponentRef, !disable);
            }
            return { value: newInProgress, presentationMode };
        });
    }, []);
    const buildActionIconChangeHoverState = useCallback((mouseEnter) => () => {
        var _a;
        activeIconWithClickStopPropagationRef.current = mouseEnter;
        const graphDivData = get((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.div, "data-this");
        if (graphDivData && isFunction(graphDivData.mouseDownEvent)) {
            if (!graphDivDataMouseDownEventRef.current)
                graphDivDataMouseDownEventRef.current = graphDivData.mouseDownEvent;
            graphDivData.mouseDownEvent = activeIconWithClickStopPropagationRef.current
                ? noop
                : graphDivDataMouseDownEventRef.current;
        }
    }, []);
    const pasteToDocumentGraphData = useCallback((data) => {
        if (!props.onPasteGraphData || !canEditAndReadyForModifyRef.current)
            return;
        setGraphInProgress(true);
        props.onPasteGraphData(data, (updates) => {
            var _a;
            if (!((_a = divRef.current) === null || _a === void 0 ? void 0 : _a.isConnected))
                return;
            applyUpdates(graphComponentRef, props.documentObjects, updates, layoutAlgorithmRef, nodesLayoutsRef, setGraphInProgress, () => {
                var _a;
                navigateToNode(graphComponentRef, (_a = head(values(updates.NodesSource))) === null || _a === void 0 ? void 0 : _a.NodeId, false);
            });
            setGraphInProgress(false);
        }, () => {
            setGraphInProgress(false);
        });
    }, [props.documentObjects, props.onPasteGraphData]);
    useEffect(() => {
        if (props.documentId &&
            props.documentObjects &&
            !graphComponentRef.current &&
            divRef.current &&
            divRef.current.isConnected &&
            !divRef.current.hasChildNodes()) {
            setGraphInProgress(true);
            graphComponentRef.current = createGraphComponent();
            setGraphComponent(graphComponentRef.current);
            initializeDefaultStyles(graphComponentRef.current, (canvasComponent, label, element) => {
                var _a, _b;
                if (!element || !element.parentElement)
                    return;
                const tag = (_a = label.owner) === null || _a === void 0 ? void 0 : _a.tag;
                if (!tag || !tag.GroupId || tag.PinnedToGroup)
                    return;
                const zoomLevel = (_b = element.parentElement) === null || _b === void 0 ? void 0 : _b.getAttribute("data-zoom-level");
                if (zoomLevel === ZOOM_LEVEL.LOW)
                    return;
                let wrapper = element.nextSibling;
                if (!wrapper) {
                    wrapper = document.createElement("div");
                    wrapper.style.setProperty("position", "relative");
                    element.parentElement.prepend(wrapper);
                }
                const isExporting = Boolean(canvasComponent === null || canvasComponent === void 0 ? void 0 : canvasComponent.div.classList.contains(GRAPH_IS_EXPORTING_CLASS_NAME));
                createRoot(wrapper).render(React.createElement(ThemeProvider, { theme: theme },
                    React.createElement(UnPinIcon, { isExporting: isExporting, disableHint: isExporting || !canEditAndReadyForModifyRef.current, positionRight: -10, positionTop: -10, iconSize: 28, data: tag, onUnPin: (nodeSource) => {
                            nodeSource.GroupId &&
                                canEditAndReadyForModifyRef.current &&
                                props.onGetGraphGroupsNodes &&
                                buildOnRowsPinCallback({
                                    graphComponentRef,
                                    sourceDataRef,
                                    documentId: props.documentId,
                                    documentObjects: props.documentObjects,
                                    layoutAlgorithmRef,
                                    setInProgress: setGraphInProgress,
                                    nodeIdsPinnedOutOfGroupRef,
                                    onGetGraphGroupsNodes: props.onGetGraphGroupsNodes
                                })([nodeSource], [nodeSource.GroupId], false);
                            buildActionIconChangeHoverState(false)();
                        }, onMouseEnter: buildActionIconChangeHoverState(true), onMouseLeave: buildActionIconChangeHoverState(false) })));
            }, (canvasComponent, label, element) => {
                var _a, _b;
                let wrapper = element === null || element === void 0 ? void 0 : element.firstChild;
                if (!wrapper)
                    wrapper =
                        label.tag && isString(label.tag)
                            ? document.querySelector(`#${label.tag}`)
                            : null;
                if (!(wrapper === null || wrapper === void 0 ? void 0 : wrapper.id))
                    return;
                const foreignObject = (_a = wrapper.parentElement) === null || _a === void 0 ? void 0 : _a.parentElement;
                const zoomLevel = foreignObject === null || foreignObject === void 0 ? void 0 : foreignObject.getAttribute("data-zoom-level");
                if (zoomLevel !== ZOOM_LEVEL.HIGH)
                    return;
                const tag = (_b = label.owner) === null || _b === void 0 ? void 0 : _b.tag;
                if (!wrapper.innerHTML) {
                    const isExporting = Boolean(canvasComponent === null || canvasComponent === void 0 ? void 0 : canvasComponent.div.classList.contains(GRAPH_IS_EXPORTING_CLASS_NAME));
                    wrapper["data-grid"] =
                        React.createRef();
                    createRoot(wrapper).render(React.createElement(ThemeProvider, { theme: theme },
                        React.createElement(GroupNodeTableIcon, { positionStyles: groupTableIconPositionStyles, onClick: () => {
                                props.documentId &&
                                    props.onGetGraphGroupsNodes &&
                                    buildOnGroupTableViewCallback(graphComponentRef, props.documentId, tag.GroupId, tag.GroupName, undefined, setGraphInProgress, nodeIdsPinnedOutOfGroupRef, props.onOpenTableViewModal, props.onGetGraphGroupsNodes);
                                buildActionIconChangeHoverState(false)();
                            }, onMouseEnter: buildActionIconChangeHoverState(true), onMouseLeave: buildActionIconChangeHoverState(false), isExporting: isExporting }),
                        React.createElement(GroupNodeLabel, { isExporting: isExporting, groupRef: wrapper["data-grid"], wrapperId: wrapper.id, tag: tag, documentObjects: props.documentObjects, onRowsPin: canEditAndReadyForModifyRef.current && props.onGetGraphGroupsNodes
                                ? buildOnRowsPinCallback({
                                    graphComponentRef,
                                    sourceDataRef,
                                    documentId: props.documentId,
                                    documentObjects: props.documentObjects,
                                    layoutAlgorithmRef,
                                    setInProgress: setGraphInProgress,
                                    nodeIdsPinnedOutOfGroupRef,
                                    onGetGraphGroupsNodes: props.onGetGraphGroupsNodes
                                })
                                : undefined, onTableEventStopPropagation: buildGroupTableEventStopPropagation(graphComponentRef, timerRef) })));
                }
                else {
                    canEditAndReadyForModifyRef.current && updateGroupTable(wrapper, tag === null || tag === void 0 ? void 0 : tag.Nodes);
                }
            });
            bindCommands(graphComponentRef, props.undoSelector, props.redoSelector);
            const graphBuilder = new GraphBuilder(graphComponentRef.current.graph);
            graphComponentRef.current.graph = graphBuilder.buildGraph();
            const inputMode = configureInputModes(graphComponentRef, Boolean(props.canEdit), Boolean(props.canEdit), activeIconWithClickStopPropagationRef);
            updateOrthogonalEdgeEditingContext(graphComponentRef, layoutAlgorithmRef);
            graphComponentRef.current.visualCaching = VisualCachingPolicy.ALWAYS;
            graphComponentRef.current.graph.undoEngineEnabled = false;
            addGraphListeners(graphComponentRef, inputMode, graphModelAppliedRef, layoutAlgorithmRef, nodeIdsPinnedOutOfGroupRef, activeIconWithClickStopPropagationRef, contextMenuRef, props.documentId, setGraphInProgress, pasteToDocumentGraphData, setHasPasteData, (key, methodsPanelRef) => (React.createElement(MethodsSubMenu, { methodsPanelRef: methodsPanelRef, key: key, onBeforeRunMethods: () => {
                    var _a, _b;
                    (_b = (_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.hide) === null || _b === void 0 ? void 0 : _b.call(_a);
                } })), props.onChangeGraphModel, handleChangeZoom, props.onChangeSelected, (documentId, nodeIds) => {
                var _a;
                (_a = props.onOpenTableViewModal) === null || _a === void 0 ? void 0 : _a.call(props, documentId, {
                    NodeIds: nodeIds,
                    GroupName: `${locale.OBJECTS}: ${nodeIds.length}`
                });
            });
            ICommand.UNDO.addCanExecuteChangedListener((sender) => {
                if (!props.documentId ||
                    !graphModelAppliedRef.current.readyForListeners ||
                    !props.canEdit ||
                    !props.onSetHasChanges)
                    return;
                props.onSetHasChanges(props.documentId, initialHasChangesRef.current || sender.canExecute(null, graphComponentRef.current));
            });
            divRef.current.appendChild(graphComponentRef.current.div);
            setGraphInProgress(false);
        }
    }, [props.documentObjects, props.documentId]);
    useEffect(() => {
        navigateToNode(graphComponentRef, props.navigateToId, true);
        props.navigateToId && props.onResetNavigateTo && props.onResetNavigateTo();
    }, [props.navigateToId]);
    useEffect(() => {
        if (!graphComponentRef.current ||
            !graphComponentRef.current.div.isConnected ||
            !sourceModelWasApplied ||
            (inProgress.value && !graphAdapterRef.current) ||
            (!props.presentationInterval && !graphAdapterRef.current))
            return;
        if (!graphAdapterRef.current) {
            graphAdapterRef.current = new YGraphAdapter(graphComponentRef.current.graph);
            setGraphInProgress(true, true);
        }
        filterGraphByInterval(graphComponentRef, graphAdapterRef, props.presentationInterval);
        if (!props.presentationInterval) {
            graphAdapterRef.current = undefined;
            setGraphInProgress(false, true);
        }
    }, [props.presentationInterval, inProgress.value, sourceModelWasApplied]);
    useEffect(() => {
        if (graphComponentRef.current && graphComponentRef.current.zoom !== zoom) {
            graphComponentRef.current.zoom = zoom;
        }
    }, [zoom]);
    useEffect(() => {
        sourceDataRef.current = props.sourceData;
    }, [props.sourceData]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (canEditAndReadyForModifyRef.current &&
            !graphAdapterRef.current &&
            props.documentId &&
            (((_a = props.sourceTaskIdsToShow) === null || _a === void 0 ? void 0 : _a.length) || ((_b = props.sourceTaskIdsToHide) === null || _b === void 0 ? void 0 : _b.length)) &&
            sourceModelWasApplied &&
            ((_c = graphComponentRef.current) === null || _c === void 0 ? void 0 : _c.graph) &&
            ((_d = divRef.current) === null || _d === void 0 ? void 0 : _d.isConnected) &&
            !inProgress.value) {
            if ((_e = props.sourceTaskIdsToHide) === null || _e === void 0 ? void 0 : _e.length) {
                props.onHideResultsSourceTaskIds &&
                    props.onHideResultsSourceTaskIds(props.documentId, props.sourceTaskIdsToHide);
                hideTasksResults(graphComponentRef, props.sourceTaskIdsToHide, nodesLayoutsRef);
            }
            if ((_f = props.sourceTaskIdsToShow) === null || _f === void 0 ? void 0 : _f.length) {
                (_g = props.onShowResultsSourceTaskIds) === null || _g === void 0 ? void 0 : _g.call(props, props.documentId, props.sourceTaskIdsToShow);
            }
        }
    }, [
        props.sourceTaskIdsToShow,
        props.sourceTaskIdsToHide,
        sourceModelWasApplied,
        inProgress.value
    ]);
    const handleChangeLayoutAlgorithm = useCallback((newLayoutAlgorithm) => {
        if (props.documentId &&
            graphComponentRef.current &&
            newLayoutAlgorithm &&
            layoutAlgorithmMap[newLayoutAlgorithm]) {
            setGraphInProgress(true);
            props.onSetLayoutAlgorithm &&
                props.onSetLayoutAlgorithm(props.documentId, newLayoutAlgorithm);
            layoutAlgorithmRef.current = newLayoutAlgorithm;
            updateOrthogonalEdgeEditingContext(graphComponentRef, layoutAlgorithmRef);
            const config = layoutAlgorithmMap[layoutAlgorithmRef.current]();
            layoutAlgorithmTimerRef.current && clearTimeout(layoutAlgorithmTimerRef.current);
            layoutAlgorithmTimerRef.current = setTimeout(() => {
                var _a;
                if (!graphComponentRef.current || !((_a = divRef.current) === null || _a === void 0 ? void 0 : _a.isConnected))
                    return;
                config.apply(graphComponentRef.current, () => {
                    setGraphInProgress(false);
                    nodesLayoutsRef.current = {};
                    updateOrthogonalEdgeEditingContext(graphComponentRef, layoutAlgorithmRef);
                }, () => {
                    props.showError && props.showError(locale.ERROR_WHILE_APPLYING_LAYOUT);
                });
            });
        }
    }, [props.documentId, props.onSetLayoutAlgorithm]);
    useEffect(() => {
        const doc = new Doc();
        const roomName = `graph_${props.documentId}`;
        const p = createProviderWebsocketProvider(roomName, doc);
        setProvider(p);
        return () => p === null || p === void 0 ? void 0 : p.destroy();
    }, [props.documentId]);
    const [hasExternalChanges, setHasExternalChanges] = useGraphChangeListener(provider);
    const notifyGraphChange = useGraphChangeNotifier(provider);
    const savingCallback = useCallback((documentId, onCompleted) => () => {
        if (canEditAndReadyForModifyRef.current &&
            readOnlyModeRef.current === READ_ONLY_MODE.READ_WRITE) {
            buildOnSaveCallback({
                graphComponentRef,
                hasChangesRef,
                documentId,
                sourceDataRef,
                graphModelAlreadyAppliedRef: graphModelAppliedRef,
                layoutAlgorithmRef,
                onChangeGraphModel: props.onChangeGraphModel,
                onSaveGraphData: props.onSaveGraphData,
                completed: () => {
                    var _a, _b, _c, _d;
                    onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted();
                    notifyGraphChange();
                    if (!((_a = divRef.current) === null || _a === void 0 ? void 0 : _a.isConnected))
                        return;
                    initialHasChangesRef.current = false;
                    (_b = props.onSetHasChanges) === null || _b === void 0 ? void 0 : _b.call(props, documentId, false);
                    (_d = (_c = graphComponentRef.current) === null || _c === void 0 ? void 0 : _c.graph.undoEngine) === null || _d === void 0 ? void 0 : _d.clear();
                }
            })();
        }
    }, [notifyGraphChange]);
    useEffect(() => {
        props.documentId &&
            props.saveTriggered &&
            canEditAndReadyForModify &&
            readOnlyModeRef.current === READ_ONLY_MODE.READ_WRITE &&
            savingCallback(props.documentId)();
    }, [props.documentId, props.saveTriggered, canEditAndReadyForModify]);
    useEffect(() => () => {
        var _a;
        if (!props.documentId)
            return;
        if (props.canEdit) {
            graphAdapterRef.current &&
                filterGraphByInterval(graphComponentRef, graphAdapterRef, undefined);
            if (readOnlyModeRef.current === READ_ONLY_MODE.READ_WRITE) {
                savingCallback(props.documentId)();
            }
            else {
                ((_a = sourceDataRef.current) === null || _a === void 0 ? void 0 : _a.LayoutAlgorithm) &&
                    props.onSetLayoutAlgorithm &&
                    props.onSetLayoutAlgorithm(props.documentId, sourceDataRef.current.LayoutAlgorithm);
                props.onSetHasChanges && props.onSetHasChanges(props.documentId, false);
            }
        }
    }, [props.documentId]);
    useEffect(() => {
        var _a;
        if (sourceModelWasApplied && props.documentId) {
            if (graphComponentRef.current)
                graphComponentRef.current.graph.undoEngineEnabled = false;
            setSourceModelWasApplied(false);
            props.canEdit && ((_a = props.onSetHasChanges) === null || _a === void 0 ? void 0 : _a.call(props, props.documentId, false));
        }
    }, [props.sourceDataKey]);
    useEffect(() => {
        if (props.documentId &&
            graphComponentRef.current &&
            props.documentObjects &&
            props.sourceData &&
            !props.saveGraphInProgress &&
            !sourceModelWasApplied) {
            setGraphInProgress(true);
            initialHasChangesRef.current = Boolean(props.hasChanges);
            layoutAlgorithmRef.current = props.layoutAlgorithm;
            if (props.initialJSONGraphModel && props.initialNodesSource && props.initialEdgesSource) {
                deserialize({
                    graphComponentRef,
                    layoutAlgorithmRef,
                    documentObjects: props.documentObjects,
                    graphModel: props.initialJSONGraphModel,
                    nodesSource: props.initialNodesSource,
                    edgesSource: props.initialEdgesSource,
                    groups: props.sourceData.Groups,
                    clearGraph: true,
                    setInProgress: setGraphInProgress
                })
                    .then(() => {
                    if (!graphComponentRef.current)
                        return;
                    if (graphComponentRef.current.graph.nodes.size === 0) {
                        graphComponentRef.current.zoom = ZOOM.DEFAULT;
                        graphComponentRef.current.viewPoint = new Point(0, 0);
                        graphComponentRef.current.updateContentRect();
                    }
                    else if (!skipFitGraphBounds.current) {
                        graphComponentRef.current.fitGraphBounds();
                    }
                    else {
                        skipFitGraphBounds.current = false;
                    }
                    graphModelAppliedRef.current.readyForListeners = true;
                    restoreSelection(graphComponentRef, props.selected);
                })
                    .finally(() => {
                    canEditAndReadyForModifyRef.current &&
                        undoEngine(graphComponentRef, nodeIdsPinnedOutOfGroupRef);
                    setSourceModelWasApplied(true);
                    setGraphInProgress(false);
                });
            }
            else {
                graphComponentRef.current.graph.clear();
                graphComponentRef.current.zoom = ZOOM.DEFAULT;
                graphComponentRef.current.viewPoint = new Point(0, 0);
                graphComponentRef.current.updateContentRect();
                graphModelAppliedRef.current.readyForListeners = true;
                canEditAndReadyForModifyRef.current &&
                    undoEngine(graphComponentRef, nodeIdsPinnedOutOfGroupRef);
                setSourceModelWasApplied(true);
                setGraphInProgress(false);
            }
        }
    }, [
        props.saveGraphInProgress,
        props.initialGraphModel,
        props.sourceData,
        props.documentObjects,
        sourceModelWasApplied
    ]);
    const addOnGraph = (data, onCompleted) => {
        var _a, _b;
        if (props.documentId &&
            sourceModelWasApplied &&
            graphComponentRef.current &&
            !props.saveGraphInProgress &&
            !inProgress.value &&
            (!isEmpty(data.NodesSource) || !isEmpty(data.EdgesSource))) {
            ((_a = contextMenuRef.current) === null || _a === void 0 ? void 0 : _a.hide) && contextMenuRef.current.hide();
            const updatesData = cloneDeep(data);
            const prevGraphEmpty = graphComponentRef.current.graph.nodes.size === 0;
            (_b = props.onClearFromFromUpdates) === null || _b === void 0 ? void 0 : _b.call(props, props.documentId, keys(updatesData.NodesSource), keys(updatesData.EdgesSource));
            applyUpdates(graphComponentRef, props.documentObjects, updatesData, layoutAlgorithmRef, nodesLayoutsRef, setGraphInProgress, () => {
                var _a, _b;
                graphModelAppliedRef.current.updates.EdgesSource = {
                    ...graphModelAppliedRef.current.updates.EdgesSource,
                    ...updatesData.EdgesSource
                };
                graphModelAppliedRef.current.updates.NodesSource = {
                    ...graphModelAppliedRef.current.updates.NodesSource,
                    ...updatesData.NodesSource
                };
                graphModelAppliedRef.current.updates.Groups = {
                    ...graphModelAppliedRef.current.updates.Groups,
                    ...updatesData.Groups
                };
                if (prevGraphEmpty) {
                    (_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.fitGraphBounds();
                }
                if (props.documentId) {
                    (_b = props.onSetHasChanges) === null || _b === void 0 ? void 0 : _b.call(props, props.documentId, true);
                    hasChangesRef.current = true;
                    savingCallback(props.documentId, onCompleted)();
                }
            }, noop, false);
        }
    };
    useEffect(() => {
        var _a, _b, _c, _d;
        if (props.documentId &&
            sourceModelWasApplied &&
            graphComponentRef.current &&
            !props.saveGraphInProgress &&
            !inProgress.value &&
            props.updates &&
            (!isEmpty((_a = props.updates) === null || _a === void 0 ? void 0 : _a.NodesSource) || !isEmpty((_b = props.updates) === null || _b === void 0 ? void 0 : _b.EdgesSource))) {
            ((_c = contextMenuRef.current) === null || _c === void 0 ? void 0 : _c.hide) && contextMenuRef.current.hide();
            const updatesData = cloneDeep(props.updates);
            const prevGraphEmpty = graphComponentRef.current.graph.nodes.size === 0;
            (_d = props.onClearFromFromUpdates) === null || _d === void 0 ? void 0 : _d.call(props, props.documentId, keys(updatesData.NodesSource), keys(updatesData.EdgesSource));
            applyUpdates(graphComponentRef, props.documentObjects, updatesData, layoutAlgorithmRef, nodesLayoutsRef, setGraphInProgress, () => {
                var _a;
                graphModelAppliedRef.current.updates.EdgesSource = {
                    ...graphModelAppliedRef.current.updates.EdgesSource,
                    ...updatesData.EdgesSource
                };
                graphModelAppliedRef.current.updates.NodesSource = {
                    ...graphModelAppliedRef.current.updates.NodesSource,
                    ...updatesData.NodesSource
                };
                graphModelAppliedRef.current.updates.Groups = {
                    ...graphModelAppliedRef.current.updates.Groups,
                    ...updatesData.Groups
                };
                if (prevGraphEmpty) {
                    (_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.fitGraphBounds();
                }
                props.canEdit &&
                    props.documentId &&
                    props.onSetHasChanges &&
                    props.onSetHasChanges(props.documentId, true);
            }, noop, false);
        }
    }, [
        props.documentId,
        props.updates,
        inProgress.value,
        props.documentObjects,
        props.saveGraphInProgress,
        sourceModelWasApplied
    ]);
    useEffect(() => {
        var _a;
        ((_a = props.sourceTaskIdsToHighlight) === null || _a === void 0 ? void 0 : _a.length) &&
            highlightTasksResults(graphComponentRef, props.sourceTaskIdsToHighlight, props.onClearSourceTaskIdsToHighlight, props.documentId);
    }, [props.sourceTaskIdsToHighlight]);
    const handleNodeWasDropped = useCallback((node) => {
        var _a, _b;
        props.onChangeDetailedPanelOpen && props.onChangeDetailedPanelOpen(undefined);
        (_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.selection.clear();
        (_b = graphComponentRef.current) === null || _b === void 0 ? void 0 : _b.selection.setSelected(node, true);
    }, [props.onChangeDetailedPanelOpen]);
    const handleDataUpdate = useCallback(buildOnNodeOrEdgeDataUpdateCallback(graphComponentRef, props.documentObjects, props.selectedObjectsType, documentNodeAttributeImageRef), [props.documentObjects, props.selectedObjectsType]);
    const handleRefresh = useCallback(() => {
        var _a;
        if (props.documentId && props.onGetGraphData) {
            (_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.selection.clear();
            props.onGetGraphData(props.documentId, false);
        }
    }, [props.documentId, props.onGetGraphData]);
    const handleRefreshWithSkipFitGraphBounds = useCallback(() => {
        skipFitGraphBounds.current = true;
        handleRefresh();
    }, [handleRefresh]);
    const getSummaryReportObjectValue = useCallback(async (summaryReportObjectType, allScreen) => {
        var _a;
        if (!graphComponentRef.current)
            return undefined;
        if (summaryReportObjectType === SUMMARY_REPORT_OBJECT_TYPE.GRAPH_SCREEN) {
            const image = await clientSideImageExport.exportImage(graphComponentRef.current.graph, !allScreen ? (_a = graphComponentRef.current.canvasContext.canvasComponent) === null || _a === void 0 ? void 0 : _a.viewport : undefined);
            return image.currentSrc;
        }
        if (summaryReportObjectType === SUMMARY_REPORT_OBJECT_TYPE.DOCUMENT_OBJECT_NODES) {
            if (!graphComponentRef.current.selection.selectedNodes.size) {
                props.showError && props.showError(locale.SELECT_NODES_FOR_ADDING_TO_SUMMARY_REPORT);
                return undefined;
            }
            return flatMap(graphComponentRef.current.selection.selectedNodes.toArray(), n => map(getGraphNodeSources(n), e => omit(e, NODE_SERVER_IGNORED_FIELDS)));
        }
        return undefined;
    }, [props.showError]);
    const handleOnRowPin = useCallback((rowsData, groupIds, pinOutOfGroup) => {
        var _a;
        if (!props.documentObjects ||
            !groupIds.length ||
            !((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph) ||
            !props.onGetGraphGroupsNodes)
            return;
        buildOnRowsPinCallback({
            graphComponentRef,
            sourceDataRef,
            documentId: props.documentId,
            documentObjects: props.documentObjects,
            layoutAlgorithmRef,
            setInProgress: setGraphInProgress,
            nodeIdsPinnedOutOfGroupRef,
            onGetGraphGroupsNodes: props.onGetGraphGroupsNodes
        })(rowsData, groupIds, pinOutOfGroup);
    }, [props.documentObjects, props.documentId]);
    const handleGetPreview = useCallback((_, nodeId, attributeId, largeAttributeId, completed, rejected) => {
        const value = documentNodeAttributeImageRef.current[nodeId]
            ? documentNodeAttributeImageRef.current[nodeId][attributeId]
            : undefined;
        if (value) {
            completed(value);
        }
        else if (largeAttributeId) {
            props.onGetPreview(largeAttributeId, completed, rejected);
        }
        else {
            rejected && rejected();
        }
    }, [props.onGetPreview]);
    const handleCopy = useCallback(() => {
        var _a;
        if (!props.documentId || !((_a = props.selected) === null || _a === void 0 ? void 0 : _a.length))
            return;
        const copyCallback = buildCopyCallback(graphComponentRef, props.documentId, setHasPasteData);
        copyCallback === null || copyCallback === void 0 ? void 0 : copyCallback();
    }, [props.documentId, Boolean((_a = props.selected) === null || _a === void 0 ? void 0 : _a.length)]);
    const handlePaste = useCallback(() => {
        if (!props.documentId || !hasPasteData)
            return;
        const pasteCallback = buildPasteCallback(graphComponentRef, props.documentId, pasteToDocumentGraphData);
        pasteCallback && pasteCallback();
    }, [props.documentId, hasPasteData, pasteToDocumentGraphData]);
    const handleDelete = useCallback(() => {
        var _a;
        if (!((_a = props.selected) === null || _a === void 0 ? void 0 : _a.length))
            return;
        const deleteCallback = buildDeleteCallback(graphComponentRef);
        deleteCallback && deleteCallback();
    }, [Boolean((_b = props.selected) === null || _b === void 0 ? void 0 : _b.length)]);
    const containerStyle = useMemo(() => ({ paddingBottom: props.bottomOffset, ...DOCUMENT_BG }), [props.bottomOffset]);
    useEffect(() => {
        props.onInProgressChanged &&
            props.onInProgressChanged(Boolean(props.inProgress || (inProgress.value && !inProgress.presentationMode)));
    }, [props.inProgress, inProgress.value, inProgress.presentationMode]);
    useEffect(() => {
        if (hasExternalChanges) {
            setHasExternalChanges(false);
            handleRefreshWithSkipFitGraphBounds();
        }
    }, [hasExternalChanges, handleRefreshWithSkipFitGraphBounds]);
    useEffect(() => {
        let timeOut;
        if (props.documentId &&
            canEditAndReadyForModify &&
            props.readOnlyMode === READ_ONLY_MODE.READ_WRITE &&
            props.hasChanges) {
            skipLoader.current = true;
            timeOut = setTimeout(() => {
                skipLoader.current = false;
            }, 500);
            savingCallback(props.documentId)();
        }
        return () => {
            clearTimeout(timeOut);
        };
    }, [props.hasChanges, props.documentId, canEditAndReadyForModify, props.readOnlyMode]);
    const dataZoom = zoom === 1 ? "original" : zoom < 1 ? "decreased" : "increased";
    return (React.createElement(ProgressOverlay, { show: !skipLoader.current &&
            (props.inProgress || (inProgress.value && !inProgress.presentationMode)), className: props.className, zIndex: parseInt(themeVariables["zindex-devextreme-dx-menu-base"]) + 1 },
        React.createElement("div", { className: styles.container, style: containerStyle },
            React.createElement(GraphPanel, { canAddToSummaryReports: !(inProgress.value && inProgress.presentationMode), canUndoOrRedo: canEditAndReadyForModify, zoom: zoom, onChangeZoom: handleChangeZoom, layoutAlgorithm: props.layoutAlgorithm, onChangeLayoutAlgorithm: canEditAndReadyForModify ? handleChangeLayoutAlgorithm : undefined, onSave: props.documentId &&
                    props.hasChanges &&
                    canEditAndReadyForModify &&
                    props.readOnlyMode === READ_ONLY_MODE.READ_WRITE
                    ? savingCallback(props.documentId)
                    : undefined, onRefresh: !props.presentationInterval ? handleRefreshWithSkipFitGraphBounds : undefined, getSummaryReportObjectValue: getSummaryReportObjectValue, onCopy: canEditAndReadyForModify && ((_c = props.selected) === null || _c === void 0 ? void 0 : _c.length) && props.documentId
                    ? handleCopy
                    : undefined, onPaste: hasPasteData && canEditAndReadyForModify && props.documentId ? handlePaste : undefined, onDelete: canEditAndReadyForModify && ((_d = props.selected) === null || _d === void 0 ? void 0 : _d.length) ? handleDelete : undefined }),
            React.createElement(RightSideDetailsPanel, { openedHeight: height ? height + (props.bottomOffset || 0) : undefined, closedHeight: props.rowActionsPanelHeight, onDataUpdate: canEditAndReadyForModify ? handleDataUpdate : undefined, canModifyAttributes: true, tableColumns: detailsPanelTableColumns, onRowsPin: canEditAndReadyForModify ? handleOnRowPin : undefined, onGetPreview: handleGetPreview, zIndex: parseInt(themeVariables["zindex-pageheader-menu"]) + 1 }),
            React.createElement(NeuralInterface, { addOnGraph: addOnGraph }),
            React.createElement("div", { className: "position-relative overflow-hidden", style: { height: "100%" }, ref: observableRef },
                !isNil(props.documentId) && (React.createElement(Droppable, { className: classNames("stretched-to-fit"), types: canEditAndReadyForModify && props.readOnlyMode === READ_ONLY_MODE.READ_WRITE
                        ? props.droppableTypes
                        : undefined, onDrop: canEditAndReadyForModify &&
                        props.readOnlyMode === READ_ONLY_MODE.READ_WRITE &&
                        graphComponentRef.current
                        ? buildOnDropCallback(graphComponentRef, handleNodeWasDropped)
                        : undefined },
                    React.createElement("div", { style: { position: "relative" } },
                        React.createElement("div", { ref: divRef, "data-zoom": dataZoom, className: "stretched-to-fit", onKeyDown: e => {
                                if (e.ctrlKey || e.metaKey) {
                                    if (e.code === "KeyC") {
                                        e.preventDefault();
                                        handleCopy();
                                    }
                                    else if (e.code === "KeyV") {
                                        e.preventDefault();
                                        handlePaste();
                                    }
                                }
                            } }),
                        React.createElement(RemoteCursorsProvider, { provider: provider },
                            React.createElement(RemoteCursorsGraph, { graph: graphComponent }))))),
                canEditAndReadyForModify && (React.createElement(Menu, { mode: "contextmenu", zIndex: parseInt(themeVariables["zindex-pageheader-menu"]) + 2, menuRef: contextMenuRef })),
                React.createElement(GroupNodeTable, { documentObjects: props.documentObjects })))));
}
export default memo(Graph);
